import { Head, Link } from "@inertiajs/react";
import { ArrowRight } from "lucide-react";

import { ActivationGuide } from "@/components/activation-guide";
import PublicHeader from "@/components/public-header";
import { SiteFooter } from "@/components/site-footer";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

interface ChangeOgImageFaq {
  question: string;
  answer: string;
}

interface ChangeOgImageWordpressProps {
  page_title?: string;
  faqs?: ChangeOgImageFaq[];
}

interface ComparisonRow {
  step: string;
  featured: string;
  seoPlugin: string;
  generated: string;
}

const COMPARISON_ROWS: ComparisonRow[] = [
  {
    step: "When it fits",
    featured: "One designed file is the whole preview, and you will maintain the theme hook.",
    seoPlugin: "Yoast or Rank Math already owns the tags, and the artwork is an upload.",
    generated: "Posts should look branded without a new file, or an agent should create the image.",
  },
  {
    step: "Where you change it",
    featured: "The post editor featured image. Then a wp_head hook if nothing else prints og:image.",
    seoPlugin: "Yoast Social tab, or Rank Math's OpenGraph image on the post.",
    generated: "The OG Pilot plugin, or a URL from the API or MCP.",
  },
  {
    step: "Who writes og:image",
    featured: "Your theme or a small custom plugin.",
    seoPlugin: "Yoast or Rank Math.",
    generated: "The OG Pilot plugin, unless Meta Ownership is External SEO plugin.",
  },
  {
    step: "When the title changes",
    featured: "The tag stays on the same file until you upload another one.",
    seoPlugin: "The tag stays on the file you uploaded for that post.",
    generated: "The plugin or API can rebuild the card from the new title and excerpt.",
  },
  {
    step: "Skip it when",
    featured: "An SEO plugin is also printing og:image.",
    seoPlugin: "You want a template, not another upload.",
    generated: "One unchanging campaign file is enough.",
  },
];

const FEATURED_IMAGE_SAMPLE = `add_action( 'wp_head', function () {
    if ( ! is_singular() || ! has_post_thumbnail() ) {
        return;
    }

    $image = wp_get_attachment_image_src( get_post_thumbnail_id(), 'full' );
    if ( ! $image ) {
        return;
    }

    $url = esc_url( $image[0] );
    echo '<meta property="og:image" content="' . $url . '" />' . "\\n";
    echo '<meta name="twitter:card" content="summary_large_image" />' . "\\n";
    echo '<meta name="twitter:image" content="' . $url . '" />' . "\\n";
} );`;

const API_SAMPLE = `POST https://ogpilot.com/api/v1/images
Content-Type: application/json
Accept: application/json

{"token":"YOUR_JWT_TOKEN"}

Required JWT claims: iss (verified domain), sub (API key prefix).
If iat is present, the image cache is valid for 24 hours from that
timestamp. Omit iat and the cache does not expire on its own.
Prefer an official SDK. Docs: https://ogpilot.com/docs`;

const WORDPRESS_API_SAMPLE = `POST https://ogpilot.com/api/v1/wordpress/images
Authorization: Bearer WORDPRESS_ACCESS_TOKEN
Accept: application/json

template=blog_post
path=/posts/hello-world
title=Hello World
description=From WordPress

The official plugin sends this after OAuth.
The bearer token is the connection access token, not a JWT.
Do not paste it into the theme.
A missing or invalid token returns 401 unauthorized.
An inactive subscription returns 403 subscription_required.`;

const MCP_SAMPLE = `Remote MCP URL: https://ogpilot.com/mcp
Transport: Streamable HTTP
Auth: OAuth 2.1 with PKCE
Docs: https://ogpilot.com/docs/mcp

generate_og_image
{ "url": "https://example.com/your-post" }

Optional style. Default is auto-detect.
Returns image_url.
generate_og_image needs a signed-in account with an active plan.
A trial is not enough.`;

const sectionClass =
  "mt-16 rounded-3xl border border-orange-200/80 bg-white/90 p-6 shadow-xl sm:p-8 dark:border-orange-500/25 dark:bg-slate-900/80 dark:shadow-none";

const linkClass = "font-medium text-orange-700 hover:underline dark:text-orange-300";

function CodeSample({ label, code }: { label: string; code: string }) {
  return (
    <div className="mt-6">
      <p className="mb-2 text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</p>
      <pre className="overflow-x-auto rounded-2xl border border-orange-200 bg-orange-50 p-5 text-sm leading-relaxed text-slate-900 dark:border-orange-500/30 dark:bg-slate-950 dark:text-orange-50">
        <code>{code}</code>
      </pre>
    </div>
  );
}

export default function ChangeOgImageWordpress({
  page_title: pageTitle = "Change OG Image WordPress",
  faqs = [],
}: ChangeOgImageWordpressProps) {
  return (
    <>
      <Head title={pageTitle} />

      <div className="min-h-screen bg-[radial-gradient(circle_at_0%_10%,#fed7aa_0,transparent_45%),radial-gradient(circle_at_100%_0%,#fecaca_0,transparent_38%),linear-gradient(180deg,#fff7ed_0%,#ffffff_32%,#fffbeb_100%)] pb-20 dark:bg-[radial-gradient(circle_at_0%_10%,#431407_0,transparent_45%),radial-gradient(circle_at_100%_0%,#7f1d1d_0,transparent_38%),linear-gradient(180deg,#0f172a_0%,#111827_32%,#0b1120_100%)]">
        <div className="container mx-auto max-w-6xl px-4 py-12">
          <PublicHeader />

          <section className="relative mt-8 overflow-hidden rounded-[2.25rem] border border-orange-200/80 bg-white/85 p-8 shadow-[0_35px_70px_-40px_rgba(194,65,12,0.55)] backdrop-blur sm:p-10 dark:border-orange-500/30 dark:bg-slate-900/80 dark:shadow-[0_35px_70px_-40px_rgba(249,115,22,0.35)]">
            <div className="relative">
              <Badge className="mb-5 border border-orange-200 bg-orange-100 px-4 py-1.5 tracking-[0.22em] text-orange-700 uppercase dark:border-orange-500/40 dark:bg-orange-500/20 dark:text-orange-100">
                WordPress how-to
              </Badge>
              <h1 className="max-w-4xl text-4xl leading-tight font-bold text-balance text-slate-900 sm:text-5xl dark:text-slate-100">
                How to change og image WordPress
              </h1>
              <p className="mt-6 max-w-3xl text-base leading-relaxed text-slate-700 sm:text-lg dark:text-slate-300">
                To <strong>change og image wordpress</strong>, pick one writer for the tag. You add og image to
                wordpress in one of three places: the featured image, an SEO-plugin social image, or a generated card.
                WordPress does not turn the featured image into <code>og:image</code> by itself. This page is the
                how-to. The plugin comparison stays on the{" "}
                <Link href="/wordpress" className={linkClass}>
                  WordPress lander
                </Link>
                .
              </p>
              <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600 dark:text-slate-400">
                The query how to add og image in wordpress is the same job: wordpress add og images in the post editor,
                in the SEO plugin, or by generating a card. An open graph image wordpress site and a wordpress open
                graph image both fail when two plugins print <code>og:image</code>, or when the tag points at a file the
                crawler cannot fetch.
              </p>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">When each path fits</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Use one row of this table, not all three at once. The featured image and the SEO-plugin field both point
              at a file you uploaded. The generated card is a template filled from the post.
            </p>
            <div className="mt-6 overflow-x-auto">
              <table className="w-full min-w-[48rem] border-collapse text-left text-sm text-slate-700 dark:text-slate-300">
                <thead>
                  <tr className="border-b border-orange-200 dark:border-orange-500/30">
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100"> </th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Featured image</th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">
                      SEO-plugin social image
                    </th>
                    <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">Generated card</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARISON_ROWS.map((row) => (
                    <tr key={row.step} className="border-b border-orange-100 align-top dark:border-orange-500/20">
                      <th className="px-3 py-3 font-semibold text-slate-900 dark:text-slate-100">{row.step}</th>
                      <td className="px-3 py-3">{row.featured}</td>
                      <td className="px-3 py-3">{row.seoPlugin}</td>
                      <td className="px-3 py-3">{row.generated}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Change the featured image</h2>
            <ol className="mt-4 list-decimal space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>
                Open the post or page and set the featured image. WordPress stores that attachment as the post
                thumbnail.
              </li>
              <li>Leave the theme hook out if Yoast, Rank Math, or another plugin already prints og:image.</li>
              <li>If nothing else writes the tag, print the attachment URL from wp_head. Use the full size.</li>
              <li>
                Publish, then open the permalink in the{" "}
                <Link href="/open-graph-debugger" className={linkClass}>
                  Open Graph debugger
                </Link>
                .
              </li>
            </ol>
            <CodeSample
              label="Theme hook you maintain. WordPress core does not print this."
              code={FEATURED_IMAGE_SAMPLE}
            />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              The theme handbook describes featured images as pictures a theme prints with{" "}
              <code>the_post_thumbnail()</code> after <code>add_theme_support(&apos;post-thumbnails&apos;)</code>. It
              does not document an <code>og:image</code> tag. The URL has to be absolute https.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Set the SEO-plugin social image</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Yoast&apos;s{" "}
              <a
                href="https://developer.yoast.com/features/opengraph/functional-specification/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph functional spec
              </a>{" "}
              sets <code>og:image</code> from a user-defined Facebook image in the Social tab, then the featured image
              on posts, then a prominent image from the content, then the social default from Search Appearance, then
              the site fallback. The current help article labels the editor Social media appearance. Rank Math&apos;s{" "}
              <a
                href="https://rankmath.com/kb/open-graph-meta-tags/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                Open Graph guide
              </a>{" "}
              lists the image as the Social-tab OpenGraph image, then the featured image, then the first image in the
              post, then the default OpenGraph thumbnail.{" "}
              <a href="https://rankmath.com/kb/titles-and-meta/" className={linkClass} target="_blank" rel="noreferrer">
                Titles and Meta
              </a>{" "}
              recommends 1200 by 630 for that thumbnail.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Set the social image on the post when this share should differ from the featured image. Set the sitewide
              default when most URLs should share one file. Confirm a single <code>og:image</code> in the debugger.
              These plugins attach the tag to a file you already have. They do not draw a new card when the headline
              changes.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Use a generated card</h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Use this path when the open graph image should follow the title, excerpt, or product fields. Install OG
              Pilot Dynamic Open Graph Images from the{" "}
              <Link href="/wordpress" className={linkClass}>
                WordPress lander
              </Link>
              , activate it, and choose Connect with OG Pilot. The listing says the plugin uses OAuth with PKCE, so you
              do not paste a long-lived API secret into the theme. Public templates named on the listing are page,
              blog_post, podcast, portfolio, book, event, product, company, and github. If Yoast, Rank Math, or AIOSEO
              should keep the tags, set Meta Ownership to External SEO plugin.
            </p>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Outside WordPress, sign the request on your server. The comparison with <code>@vercel/og</code> and a DIY
              Satori pipeline is on the{" "}
              <Link href="/og-image-api" className={linkClass}>
                OG image API page
              </Link>
              . That is the page to use when the fix is a generated card rather than another upload.
            </p>
            <CodeSample label="Public API. Sign the JWT on your server." code={API_SAMPLE} />
            <CodeSample label="WordPress namespace. The plugin sends this after OAuth." code={WORDPRESS_API_SAMPLE} />
            <CodeSample label="MCP. generate_og_image takes a public URL." code={MCP_SAMPLE} />
            <p className="mt-4 max-w-3xl text-sm leading-6 text-slate-600 dark:text-slate-300">
              The{" "}
              <a
                href="https://wordpress.org/plugins/og-pilot-dynamic-open-graph-images/"
                className={linkClass}
                target="_blank"
                rel="noreferrer"
              >
                plugin FAQ
              </a>{" "}
              says most images are ready within 5 to 10 minutes depending on queue load and the site traffic that
              triggers WP-Cron. The plan catalog lists Starter at $5/month ($50/year, 10,000-image quota), Builder at
              $49/month ($99/year, 100,000-image quota), and Growth at $99/month ($199/year, unlimited image quota).
              Those defaults are on{" "}
              <Link href="/#pricing" className={linkClass}>
                pricing
              </Link>
              . A Stripe image_quota feature can replace the quota. The catalog has no per-image price.
            </p>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Where to add og images for social media on a WordPress site
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              The query where to add og images for social media on wordpress site is about the editor, not a second tag
              name. The HTML location is always the document head. Wordpress add og images in the editor only after you
              know who prints the tag.
            </p>
            <ul className="mt-4 list-disc space-y-2 pl-5 text-base leading-7 text-slate-700 dark:text-slate-300">
              <li>Featured image: the post or page sidebar. Core will not copy that file into og:image.</li>
              <li>
                SEO plugin: the social image field on that post, and the sitewide fallback in the plugin settings.
              </li>
              <li>
                Generated card: Settings, OG Pilot Open Graph Images, or the API and MCP samples above. A wordpress
                plugin for og tags is whichever plugin you let own the head.
              </li>
            </ul>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              Yoast, Rank Math, and AIOSEO are upload-based. OG Pilot is the generated-card plugin. Running two of them
              without turning one off produces duplicate <code>og:image</code> tags. After you change the file, change
              the URL or force a rescrape. Platforms cache the bytes, not the WordPress editor.
            </p>
          </section>

          <section className="mt-16 rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-orange-900 p-8 text-white shadow-xl sm:p-10">
            <h2 className="text-3xl font-bold">Get an API key, install MCP, or use the plugin</h2>
            <p className="mt-4 max-w-2xl text-sm leading-6 text-slate-200">
              The steps above are the change. An API key signs the CDN URL. MCP lets an agent call generate_og_image.
              The WordPress plugin is the path when the library is posts, pages, and WooCommerce.
            </p>
            <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <Button asChild size="lg" className="bg-white text-slate-900 hover:bg-slate-100">
                <Link href="/sign_up">
                  Get an API key
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/docs/mcp">Install MCP</Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white/40 bg-transparent text-white hover:bg-white/10"
              >
                <Link href="/wordpress">WordPress plugin</Link>
              </Button>
            </div>
          </section>

          <section className={sectionClass}>
            <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
              Open Graph tags, next to this how-to
            </h2>
            <p className="mt-4 max-w-3xl text-base leading-7 text-slate-700 dark:text-slate-300">
              What <code>og:title</code>, <code>og:description</code>, <code>og:image</code>, and <code>og:url</code>{" "}
              mean in a theme or plugin is a separate explainer, and that page is not published yet. Until it is, use
              the{" "}
              <Link href="/wordpress" className={linkClass}>
                WordPress lander
              </Link>{" "}
              for which tags the plugin writes and when to leave them to Yoast or Rank Math. This page only covers
              changing the image.
            </p>
          </section>

          <ActivationGuide
            title="Check the permalink, then pick the writer"
            links={[
              {
                href: "/wordpress",
                label: "WordPress plugin",
                detail: "Install the plugin for posts, pages, and WooCommerce.",
              },
              {
                href: "/sign_up",
                label: "Get an API key",
                detail: "Sign CDN URLs from a server that is not the WordPress plugin.",
              },
              {
                href: "/docs/mcp",
                label: "Install MCP",
                detail: "Call generate_og_image from an agent that already has the permalink.",
              },
              {
                href: "/open-graph-debugger",
                label: "Open Graph debugger",
                detail: "Confirm a single og:image before Facebook, X, LinkedIn, or Slack cache it.",
              },
              {
                href: "/og-image-api",
                label: "OG image API",
                detail: "Hosted API next to @vercel/og and a DIY Satori pipeline.",
              },
              {
                href: "/docs",
                label: "API docs",
                detail: "JWT claims, the PHP SDK, and the 24-hour iat cache rule.",
              },
            ]}
          >
            <p>
              After the tag is live, run the URL through the{" "}
              <Link href="/open-graph-debugger" className={linkClass}>
                Open Graph debugger
              </Link>
              . The plugin path is{" "}
              <Link href="/wordpress" className={linkClass}>
                /wordpress
              </Link>
              . The generated-card comparison is{" "}
              <Link href="/og-image-api" className={linkClass}>
                /og-image-api
              </Link>
              .
            </p>
          </ActivationGuide>

          {faqs.length > 0 ? (
            <section className={sectionClass}>
              <h2 className="text-3xl font-bold text-slate-900 dark:text-slate-100">
                Change og image wordpress questions
              </h2>
              <div className="mt-6 space-y-6">
                {faqs.map((faq) => (
                  <article key={faq.question}>
                    <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{faq.question}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{faq.answer}</p>
                  </article>
                ))}
              </div>
            </section>
          ) : null}

          <SiteFooter className="mt-16" showSeparator={false} />
        </div>
      </div>
    </>
  );
}
